import { Button, Input } from "components";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import { getAuthUrl } from "utils/api-util";

const AddMeters = ({ makeTabActive }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [disabledIndexes, setDisabledIndexes] = useState([]); // Tracks which fields are disabled

  // Get the user
  const user = useSelector((state) => state.auth.userData);

  // Check if just a building is being created
  const isCreateNewBuilding = localStorage.getItem("createNewBuilding");

  const handleCheckboxChange = (index) => {
    const updatedFormData = [...formData];
    updatedFormData[index].isChecked = !updatedFormData[index].isChecked;
    setFormData(updatedFormData);

    // check the type of user
    if (user?.role === "ROLE_SUPER_ADMIN") {
      // Save to localStorage (optional, if needed)
      localStorage.setItem(
        "addMetersFormData",
        JSON.stringify(updatedFormData)
      );
    } else if (user?.role === "ROLE_ADMIN") {
      // Save to localStorage (optional, if needed)
      localStorage.setItem(
        "addAdminMetersFormData",
        JSON.stringify(updatedFormData)
      );
    }
  };

  // get the number of buildings from the customer
  const buildingInfo =
    JSON.parse(localStorage.getItem("buildingInfo")) ||
    JSON.parse(localStorage.getItem("buildingAdminInfo"));
  const { numberOfApartments } = buildingInfo;
  const createArray = (num) => {
    return Array.from({ length: num }, (_, i) => i + 1);
  };
  const buildings = createArray(numberOfApartments);

  const [formData, setFormData] = useState(
    buildings.map(() => ({
      deviceId: "",
      deviceAddress: "",
      tariff: "",
      deviceModel: "2",
      isChecked: false,
    }))
  ); // Initializing form data for all buildings
  const [errors, setErrors] = useState([]); // Storing errors for validation

  // Load data from local storage on mount
  useEffect(() => {
    if (user?.role === "ROLE_SUPER_ADMIN") {
      const savedData = localStorage.getItem("addMetersFormData");
      if (savedData) {
        setFormData(JSON.parse(savedData));
      }
    } else if (user?.role === "ROLE_ADMIN") {
      const savedData = localStorage.getItem("addAdminMetersFormData");
      if (savedData) {
        setFormData(JSON.parse(savedData));
      }
    }
  }, []);

  const handleInputChange = (index, field, value) => {
    const updatedFormData = [...formData];

    // Checks for duplicates
    if (field === "deviceId") {
      // Check if the deviceId already exists in other fields
      const isDuplicate = formData.some(
        (item, idx) => item.deviceId === value && idx !== index
      );

      if (isDuplicate) {
        setError(
          `Device ID "${value}" is already in use. Please enter a unique value.`
        );
        return; // Prevent further updates for duplicate values
      }
    }

    if (field === "deviceAddress") {
      // Check if the deviceId already exists in other fields
      const isDeviceAddressDuplicate = formData.some(
        (item, idx) => item.deviceAddress === value && idx !== index
      );

      if (isDeviceAddressDuplicate) {
        setError(
          `Device Address "${value}" is already in use. Please enter a unique value.`
        );
        return; // Prevent further updates for duplicate values
      }
    }

    updatedFormData[index][field] = value;
    setFormData(updatedFormData);
  };

  // validate form inputs
  const validateForm = () => {
    const validationErrors = [];
    formData.forEach((buildingData, index) => {
      if (!buildingData.deviceId)
        validationErrors.push(
          `Device ID is required for Building ${index + 1}`
        );
      if (!buildingData.deviceAddress)
        validationErrors.push(
          `Device Address is required for Building ${index + 1}`
        );
      if (!buildingData.tariff)
        validationErrors.push(`Tariff is required for Building ${index + 1}`);
      if (!buildingData.deviceModel)
        validationErrors.push(
          `Device Model is required for Building ${index + 1}`
        );
    });
    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  // submit the user entered inputs and move to next screen. Valaidate inputs as well
  const handleSubmit = () => {
    if (validateForm()) {
      if (isCreateNewBuilding) {
        makeTabActive(4);
        return;
      }
      if (user?.role === "ROLE_SUPER_ADMIN") {
        localStorage.setItem("addMetersFormData", JSON.stringify(formData));
        makeTabActive(5);
      } else if (user?.role === "ROLE_ADMIN") {
        localStorage.setItem(
          "addAdminMetersFormData",
          JSON.stringify(formData)
        );
        makeTabActive(4);
      }
    } else {
      setError("Please fill out all required fields.");
    }
  };

  const token = window.localStorage.getItem("token");
  const baseUrl = getAuthUrl();

  const onHandleBlur = async (index) => {
    const { deviceId, isChecked } = formData[index];

    if (!deviceId) {
      setErrors([`Device ID is required for Building ${index + 1}`]);
      return;
    }

    if (!isChecked) {
      // Debugging: Log the state if necessary
      console.log(`Building ${index + 1}: isChecked is false`);
      setError("");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(`${baseUrl}/device/meter/${deviceId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (response.status === 200) {
        const updatedFormData = [...formData];
        updatedFormData[index] = {
          ...updatedFormData[index],
          deviceAddress: data.deviceAddress || "",
          tariff: data.tariff || "",
          deviceModel: data.deviceModel || "",
        };
        setFormData(updatedFormData);
        if (user?.role === "ROLE_SUPER_ADMIN") {
          localStorage.setItem(
            "addMetersFormData",
            JSON.stringify(updatedFormData)
          );
        } else if (user?.role === "ROLE_ADMIN") {
          localStorage.setItem(
            "addAdminMetersFormData",
            JSON.stringify(updatedFormData)
          );
        }
      }
    } catch (error) {
      console.error(error);
      setError("Invalid meter device Id");
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    if (isCreateNewBuilding) {
      makeTabActive(2);
      return;
    }
    if (user?.role === "ROLE_SUPER_ADMIN") {
      makeTabActive(3);
    } else if (user?.role === "ROLE_ADMIN") {
      makeTabActive(2);
    }
  };

  return (
    <>
      <form>
        <div className="mt-4">
          <h3 className="building-header">Add Meters</h3>
        </div>

        {buildings.map((building, index) => (
          <div key={index}>
            <h4 className="font-bold my-3 text-lg">Unit {building}</h4>
            <div className="flex my-4">
              <input
                id="check"
                type="checkbox"
                name=""
                checked={formData[index]?.isChecked}
                onChange={() => handleCheckboxChange(index)}
              />
              <label htmlFor="check" className="ml-2">
                Use Existing Meter
              </label>
            </div>
            <div key={index} className="customer-form-wrapper ">
              <Input
                wrapClassName="bg-gray_100 border-[1px] border-light_blue_800 border-solid flex flex-row font-montserrat h-[66px] items-center justify-start  px-[20px] py-[11px] rounded-[10px] w-full"
                className="font-semibold text-gray_600 text-[16px] w-full"
                name={`device-id-${index}`}
                value={formData[index]?.deviceId || ""}
                onChange={(e) =>
                  handleInputChange(index, "deviceId", e.target.value)
                }
                onBlur={() => onHandleBlur(index)}
                labelText="Device ID"
                disabled={disabledIndexes.includes(index)}
              />
              <Input
                wrapClassName="bg-gray_100 border-[1px] border-light_blue_800 border-solid flex flex-row font-montserrat h-[66px] items-center justify-start  px-[20px] py-[11px] rounded-[10px] w-full"
                className="font-semibold text-gray_600 text-[16px] w-full"
                name={`device-address-${index}`}
                type="string"
                value={formData[index]?.deviceAddress || ""}
                onChange={(e) =>
                  handleInputChange(index, "deviceAddress", e.target.value)
                }
                disabled={disabledIndexes.includes(index)}
                labelText="Device Address"
              />
              <Input
                wrapClassName="bg-gray_100 border-[1px] border-light_blue_800 border-solid flex flex-row font-montserrat h-[66px] items-center justify-start  px-[20px] py-[11px] rounded-[10px] w-full"
                className="font-semibold text-gray_600 text-[16px] w-full"
                name={`tariff-${index}`}
                type="number"
                value={formData[index]?.tariff || ""}
                onChange={(e) =>
                  handleInputChange(index, "tariff", e.target.value)
                }
                disabled={disabledIndexes.includes(index)}
                labelText="Tariff"
              />
              <select
                value={formData[index]?.deviceModel || ""}
                disabled={disabledIndexes.includes(index)}
                onChange={(e) =>
                  handleInputChange(index, "deviceModel", e.target.value)
                }
                className="w-full text-sm bg-gray_100 border-[1px] text-[16px] text-gray_600 font-semibold outline-none border-light_blue_800 font-montserrat h-[66px]  rounded-[10px] py-[11px]"
              >
                <option value="2">ACCUREV1300</option>
                <option value="3">ACCUREV1400</option>
                <option value="4">EKM-OMULv4</option>
                <option value="5">EKM-OMIIULv3</option>
              </select>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex items-center mt-3 gap-2">
            <ClipLoader color="#008be0" size={20} />
            <p className="text-[green] text-[12px]"> Please wait</p>
          </div>
        )}
        <p className="text-[red] mt-3 text-[12px]">{error}</p>
      </form>
      <div className="mt-4">
        <Button
          onClick={goBack}
          className="border-[1px] mr-4 border-light_blue_800 bg-white cursor-pointer font-extrabold h-[64px] mb-[20px] px-[7px] sm:px-[15px] sm:py-[5px] rounded-[10px] text-[14px] text-center text-light_blue_800 w-[150px]"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          className="bg-light_blue_800 cursor-pointer font-extrabold h-[64px] mb-[20px] px-[7px] sm:px-[15px] sm:py-[5px] rounded-[10px] text-[14px] text-center text-white_A700 w-[150px]"
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default AddMeters;
